import { Injectable, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { OCC_USER_ID_ANONYMOUS, UserIdService } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { LoadInvoicesDocumentTypes } from '../custom-invoice-history/store/actions/custom-invoices-document-types.actions';
import { LoadInvoices } from '../custom-invoice-history/store/actions/custom-invoices-history.actions';
import { getInvoicesDocumentTypeValue } from '../custom-invoice-history/store/selectors/custom-invoice-document-type.selector';
import {
  getInvoicesHistoryError,
  getInvoicesHistoryLoader,
  getInvoicesHistoryState,
  getInvoicesHistorySuccess,
} from '../custom-invoice-history/store/selectors/custom-invoice-history.selector';
import { InvoiceHistoryList, InvoicesFilter } from '../invoices.model';
import { StateWithInvoices } from '../store/selectors/custom-invoice.selector';

@Injectable({
  providedIn: 'root'
})
export class CustomInvoiceHistoryService implements OnDestroy {
  protected subscriptions: Subscription = new Subscription();

  constructor(
    protected store: Store<StateWithInvoices>,
    protected userIdService: UserIdService
  ) { }

  /**
   * Retrieves an order list
   */
  loadDocumentTypeList(): void {
    this.store.dispatch(new LoadInvoicesDocumentTypes());
  }

  getDocumentTypeList(): Observable<any> {
    return this.store.pipe(select(getInvoicesDocumentTypeValue));
  }

  getInvoicesHistoryLoaderState(): Observable<boolean> {
    return this.store.pipe(select(getInvoicesHistoryLoader));
  }

  getInvoicesHistorySuccessState(): Observable<boolean> {
    return this.store.pipe(select(getInvoicesHistorySuccess));
  }

  getInvoicesHistoryFailState(): Observable<boolean> {
    return this.store.pipe(select(getInvoicesHistoryError));
  }

  getInvoicesHistoryList(pageSize: number): Observable<InvoiceHistoryList> {
    return this.store.pipe(
      select(getInvoicesHistoryState),
      tap((invoiceListState) => {
        const attemptedLoad =
          invoiceListState.loading ||
          invoiceListState.success ||
          invoiceListState.error;
        if (!attemptedLoad) {
          this.loadInvoiceList(pageSize);
        }
      }),
      map((orderListState) => orderListState.value)
    );
  }

  /**
   * Retrieves an order list
   * @param pageSize page size
   * @param currentPage current page
   * @param sort sort
   * @param invoicesFilter extra filters
   */
  loadInvoiceList(pageSize: number, currentPage?: number, sort?: string, invoicesFilter?: InvoicesFilter): void {
    this.subscriptions.add(this.userIdService.takeUserId(false).subscribe(
      userId => {
        if (userId !== OCC_USER_ID_ANONYMOUS) {
          this.store.dispatch(
            new LoadInvoices({
              userId,
              pageSize,
              currentPage,
              sort,
              invoicesFilter,
            })
          );
        }
      }));
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
